import React, { lazy, Suspense, useEffect, useState, useMemo } from "react";
import { Container, Row, Col } from "react-bootstrap";
import { useParams, useSearchParams } from "react-router-dom";
import { Stepper } from "clayco-stepper";
import Spinner from "./shared/spinner";
import { useLocation, useNavigate } from "react-router-dom";

import { CdsStepIndicator, CdsProgressBar } from "clayco-component-library";
import ApiProvider from "../../apiProvider";
import { useMsal } from "@azure/msal-react";
import EmployeeHelper from "../../Components/employeeUtility";
import {
    tabDetailSlim,
    tabDetailFull,
    stepperSlim,
    stepperFull,
} from "../../navigationConstants";
import { useEmployeeNumber } from "../../employeeNumber";
import OnBehalfOfControl from "../../Components/onBehalfOfControl";

const EducationView = lazy(() => import("./education/education-view"));
const CertificationsView = lazy(() => import("./certifications/certifications"));
const EmploymentHistoryView = lazy(() =>
    import("./employment-history/employment-history")
);
const CommunityView = lazy(() => import("./community/community-view"));
const MediaRelationsView = lazy(() => import("./media/media-relations"));
const AwardsView = lazy(() => import("./awards/awards"));
const PreviousProjectsView = lazy(() =>
    import("./previous-projects/previous-projects")
);


const SkillsExperience = ({
    setLocation,
    status,
    statusLoading,
    skipStepHistory,
    setSkipStepHistory,
    loadPreviousProjects,
    loadOboControl,
    setMenuOptions,
    setMenuOverride
}) => {
    const [tabNavDetails, setTabNavDetails] = useState(
        loadPreviousProjects ? tabDetailFull : tabDetailSlim
    );
    const { instance, accounts } = useMsal();
    const {employeeNumber} = useEmployeeNumber();
    const apiProvider = new ApiProvider(instance, accounts[0]);
    let { step } = useParams();
    const [queryParameters] = useSearchParams();
    const [key, setKey] = useState(queryParameters.get("step"));
    const { search } = useLocation();
    const [submitComplete, setSubmitComplete] = useState(false);
    const [load, setLoad] = useState(false);
    const content = useMemo(() => (<View />), [employeeNumber, load, key, submitComplete]);
    const [steps, setSteps] = useState(
        loadPreviousProjects ? stepperFull : stepperSlim
    );

    // seemingly wrong, except useState is too slow and isNavigating will always lag behind. Preventing the rat's nest from executing correctly
    var isNavigating = false;

    const showStepper =
        localStorage.getItem("submitComplete") === "true" ? false : true;

    useEffect(()=>{
        if (isNavigating){
            return;
        }

        setLoad(true);

        let destination = window.location.pathname;
        if (destination !== "/SkillsLanding" && destination.indexOf("Skills") >= 0){
            destination = destination.split("/");
            destination = destination[destination.length - 1];
            navigateStepper(destination);
        }
        
    }, [window.location.pathname]);

    useEffect(() => {
        if (status) {
            updateStepperOptions();
        }
    }, [status]);
    

    useEffect(() => {
        setMenuOptions();
    }, [employeeNumber]);

    function stepperLinkClicked(key) {
        let destination = "";

        switch (key) {
            case "Education & Training":
                destination = "/Skills/education";
                break;
            case "Certifications":
                destination = "/Skills/certifications";
                break;
            case "Employment History":
                destination = "/Skills/employment";
                break;
            case "Community Involvement":
                destination = "/Skills/community";
                break;
            case "Media Relations":
                destination = "/Skills/media";
                break;
            case "Awards & Achievements":
                destination = "/Skills/awards";
                break;
            case "Previous Project Experience":
                destination = "/Skills/previousProjects";
                break;
            default:
                break;
        }

        if (destination !== "") {
            setLocation(destination);
            updateStepper(destination);
            //navigate(destination);
        }
    }

    function navigateStepper(destination) {
        if (isNavigating){
            isNavigating = false;
            return;
        }

        isNavigating = true;
        setSubmitComplete(false);
        setKey(destination);
        
        updateStepper(destination);
        setLocation(`/Skills/${destination}`);
        isNavigating = false;
    }

    function onSetStepperKey(key) {
        setKey(key);

        updateStepper(key);
    }

    async function setComplete() {
        if (status?.id) {
            await apiProvider.Post("Status/Update", {
                employeeNumber: employeeNumber.number,
                completed: true,
                employeeName: employeeNumber.name,
                id: status.id,
            });
        } else {
            await apiProvider.Post("Status", {
                employeeNumber: employeeNumber.number,
                completed: true,
                employeeName: employeeNumber.name,
            });
        }

        setSubmitComplete(true);
        localStorage.setItem("submitComplete", true);
        // force refresh of key
        localStorage.removeItem("visibilityRules");
    }

    function updateStepper(key) {
        switch (key) {
            case "education":
                setActiveStep(1);
                break;
            case "certifications":
                setActiveStep(2);
                break;
            case "employment":
                setActiveStep(3);
                break;
            case "community":
                setActiveStep(4);
                break;
            case "media":
                setActiveStep(5);
                break;
            case "awards":
                setActiveStep(6);
                break;
            case "previousProjects":
                setActiveStep(7);
                break;
            default:
                setActiveStep(8);
                break;
        }
    }

    async function updateStepperOptions() {
        let showPrevProj = await EmployeeHelper.CheckLoadPreviousProjects(
            apiProvider
        );
    }

    //For CDS Step Indicator and Progress Bar (mobile)

    const [activeStep, setActiveStep] = useState(1);

    function StepIndicator() {
        return (
            <>
                <Row className="mb-3 d-flex d-lg-none">
                    <Col>
                        <CdsProgressBar
                            steps={steps}
                            activeStep={activeStep}
                        ></CdsProgressBar>
                    </Col>
                </Row>
                <Row className="mb-4 d-none d-lg-flex">
                    <Col>
                        <CdsStepIndicator
                            steps={steps}
                            activeStep={activeStep}
                            handleStepClick={stepperLinkClicked}
                        ></CdsStepIndicator>
                    </Col>
                </Row>
            </>
        );
    }
    function View() {
        if (!statusLoading) {
            if (!submitComplete) {
                return (
                    <>                      
                        {showStepper ? <StepIndicator></StepIndicator> : <></>}
                        <Stepper
                            mountOnEnter={true}
                            unmountOnExit={true}
                            tabNavDetails={tabNavDetails}
                            stepperKey={key}
                            setStepperKey={(key) => onSetStepperKey(key)}
                            tabs={[
                                <Suspense fallback={<div>Loading...</div>}>
                                    <EducationView
                                        navigateStepper={navigateStepper}
                                        skipStepHistory={skipStepHistory}
                                        setSkipStepHistory={setSkipStepHistory}
                                    ></EducationView>
                                </Suspense>,
                                <Suspense fallback={<div>Loading...</div>}>
                                    <CertificationsView
                                        navigateStepper={navigateStepper}
                                        skipStepHistory={skipStepHistory}
                                        setSkipStepHistory={setSkipStepHistory}
                                    ></CertificationsView>
                                </Suspense>,
                                <Suspense fallback={<div>Loading...</div>}>
                                    <EmploymentHistoryView
                                        navigateStepper={navigateStepper}
                                        skipStepHistory={skipStepHistory}
                                        setSkipStepHistory={setSkipStepHistory}
                                    ></EmploymentHistoryView>
                                </Suspense>,
                                <Suspense fallback={<div>Loading...</div>}>
                                    <CommunityView
                                        navigateStepper={navigateStepper}
                                        skipStepHistory={skipStepHistory}
                                        setSkipStepHistory={setSkipStepHistory}
                                    ></CommunityView>
                                </Suspense>,
                                <Suspense fallback={<div>Loading...</div>}>
                                    <MediaRelationsView
                                        navigateStepper={navigateStepper}
                                        skipStepHistory={skipStepHistory}
                                        setSkipStepHistory={setSkipStepHistory}
                                    ></MediaRelationsView>
                                </Suspense>,
                                <Suspense fallback={<div>Loading...</div>}>
                                    <AwardsView
                                        setComplete={setComplete}
                                        navigateStepper={navigateStepper}
                                        isComplete={
                                            status?.completed || submitComplete
                                        }
                                        skipStepHistory={skipStepHistory}
                                        setSkipStepHistory={setSkipStepHistory}
                                    ></AwardsView>
                                </Suspense>,
                                <Suspense fallback={<div>Loading...</div>}>
                                    <PreviousProjectsView
                                        navigateStepper={navigateStepper}
                                        setComplete={setComplete}
                                        skipStepHistory={skipStepHistory}
                                        setSkipStepHistory={setSkipStepHistory}
                                    ></PreviousProjectsView>
                                </Suspense>,
                            ]}
                        ></Stepper>
                    </>
                );
            } else {
                return (
                    <div className="completion-message">
                        <Row className="mt-5 mb-4">
                            <Col className="text-center">
                                <h1>Thank You!</h1>
                            </Col>
                        </Row>
                        <Row>
                            <Col>
                                <p className="text-center">
                                    You have successfully completed your Skills
                                    & Experience profile.
                                </p>
                            </Col>
                        </Row>
                    </div>
                );
            }
        }

        return (
            <Spinner
                show={statusLoading}
                text="Loading..."
                showContainer={false}
                showCurtain={true}
            />
        );
    }
    return (
        <>
            <Container fluid>
                {loadOboControl ?
                <OnBehalfOfControl>
                    {content}
                </OnBehalfOfControl>
                :
                <View />
                }
            </Container>
        </>
    );
};

export default SkillsExperience;
